import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  TextField,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { resolveNroSucFiltro, useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import conteoStockService, {
  extractUbicacionFromFormulario,
} from '../../services/conteoStockService';
import ubicacionService from '../../services/ubicacionService';
import { IConteoStock } from '../../interfaces/ConteoStock';

const getFirstDayOfMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

const getLastDayOfMonth = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split('T')[0];
};

const formatFecha = (fecha: string | Date | null | undefined): string => {
  if (!fecha) return '-';
  const d = typeof fecha === 'string' ? new Date(fecha) : fecha;
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
};

const formatUbicacionLabel = (
  nro: number | null | undefined,
  det: string | null | undefined,
  nombres: Record<number, string>,
): string => {
  if (nro == null || nro <= 0) {
    return det?.trim() || '-';
  }
  const nombre = det?.trim() || nombres[nro] || '';
  return nombre ? `${nombre} (${nro})` : `Ubicación ${nro}`;
};

const ConteoStock: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [conteos, setConteos] = useState<IConteoStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [fechaDesde, setFechaDesde] = useState<string>(getFirstDayOfMonth);
  const [fechaHasta, setFechaHasta] = useState<string>(getLastDayOfMonth);
  const [ubicacionesMap, setUbicacionesMap] = useState<Record<number, string>>({});
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();

  useEffect(() => {
    const loadUbicaciones = async () => {
      try {
        // Catálogo amplio para resolver nombres en el listado
        const data = await ubicacionService.getUbicaciones({ pageNumber: 1, pageSize: 500 });
        const map: Record<number, string> = {};
        data.forEach((u) => {
          if (u.nroUbicacion > 0) {
            map[u.nroUbicacion] = u.nombreUbicacion?.trim() || `Ubicación ${u.nroUbicacion}`;
          }
        });
        setUbicacionesMap(map);
      } catch {
        try {
          const data = await ubicacionService.getUbicacionesParaUsuario();
          const map: Record<number, string> = {};
          data.forEach((u) => {
            if (u.nroUbicacion > 0) {
              map[u.nroUbicacion] = u.nombreUbicacion?.trim() || `Ubicación ${u.nroUbicacion}`;
            }
          });
          setUbicacionesMap(map);
        } catch {
          setUbicacionesMap({});
        }
      }
    };
    loadUbicaciones();
  }, []);

  const loadConteos = useCallback(async () => {
    try {
      setLoading(true);
      const nroSucFiltro = resolveNroSucFiltro(filtroNroSuc);
      const pageSize = Math.min(rowsPerPage, 20);
      const response = await conteoStockService.getConteos({
        pageNumber: page + 1,
        pageSize,
        searchTerm: searchTerm.trim() || undefined,
        ...(fechaDesde ? { fechaDesde } : {}),
        ...(fechaHasta ? { fechaHasta } : {}),
        ...(nroSucFiltro != null ? { nroSuc: nroSucFiltro } : {}),
      });

      let items: IConteoStock[] = (response.items ?? []).map((c: any) => {
        const { nroUbicacion, detUbicacion } = extractUbicacionFromFormulario(c);
        return {
          ...c,
          nroFormulario: c.nroFormulario ?? c.NroFormulario,
          fecha: c.fecha ?? c.Fecha ?? c.fechaEmision ?? c.FechaEmision,
          nroSuc: c.nroSuc ?? c.NroSuc ?? null,
          descripcion: c.descripcion ?? c.Descripcion ?? null,
          nroUbicacion,
          detUbicacion,
          usuario: c.usuario ?? c.Usuario ?? null,
        };
      });

      // Si el listado no trae ubicación, enriquecer con el detalle (mismo criterio que la pantalla de ver)
      const missing = items.filter((c) => !c.nroUbicacion || c.nroUbicacion <= 0);
      if (missing.length > 0) {
        const enriched = await Promise.all(
          missing.map(async (c) => {
            try {
              const detail = await conteoStockService.getConteo(c.nroFormulario);
              const ubic = extractUbicacionFromFormulario(detail);
              return { nroFormulario: c.nroFormulario, ...ubic };
            } catch {
              return { nroFormulario: c.nroFormulario, nroUbicacion: null, detUbicacion: null };
            }
          }),
        );
        const byNro = new Map(enriched.map((e) => [e.nroFormulario, e]));
        items = items.map((c) => {
          if (c.nroUbicacion && c.nroUbicacion > 0) return c;
          const e = byNro.get(c.nroFormulario);
          if (!e) return c;
          return {
            ...c,
            nroUbicacion: e.nroUbicacion,
            detUbicacion: e.detUbicacion ?? c.detUbicacion,
          };
        });
      }

      // Resolver nombres faltantes
      const mapActual = ubicacionesMap;
      const nrosSinNombre = Array.from(
        new Set(
          items
            .filter((c) => c.nroUbicacion && c.nroUbicacion > 0 && !c.detUbicacion)
            .map((c) => c.nroUbicacion as number),
        ),
      ).filter((nro) => !mapActual[nro]);

      if (nrosSinNombre.length > 0) {
        const resolved = await Promise.all(
          nrosSinNombre.map(async (nro) => {
            try {
              const u = await ubicacionService.getUbicacion(nro);
              return { nro, nombre: u?.nombreUbicacion?.trim() || null };
            } catch {
              return { nro, nombre: null };
            }
          }),
        );
        setUbicacionesMap((prev) => {
          const next = { ...prev };
          resolved.forEach(({ nro, nombre }) => {
            if (nombre) next[nro] = nombre;
          });
          return next;
        });
        items = items.map((c) => {
          if (!c.nroUbicacion || c.detUbicacion) return c;
          const found = resolved.find((r) => r.nro === c.nroUbicacion);
          return found?.nombre ? { ...c, detUbicacion: found.nombre } : c;
        });
      }

      setConteos(items);
      setTotalCount(response.totalCount ?? 0);
    } catch (error) {
      console.error('Error al cargar conteos:', error);
      enqueueSnackbar('Error al cargar los conteos de stock', { variant: 'error' });
      setConteos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta, filtroNroSuc, enqueueSnackbar]);

  useEffect(() => {
    if (location.state?.refresh) {
      setPage(0);
      setSearchTerm('');
      setSearchInput('');
      setFechaDesde(getFirstDayOfMonth());
      setFechaHasta(getLastDayOfMonth());
      setFiltroNroSuc(null);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refresh, location.pathname, navigate, setFiltroNroSuc]);

  useEffect(() => {
    if (!filtroListo) return;
    loadConteos();
  }, [loadConteos, location.key, filtroListo]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(Math.min(value, 20));
    setPage(0);
  };

  const openDetalle = (nroFormulario: number) => {
    navigate(`/inventario/conteo/${nroFormulario}`);
  };

  const nombreSucursal = (nroSuc: number | null | undefined): string => {
    if (nroSuc == null || nroSuc <= 0) return '-';
    const suc = sucursales.find((s) => s.nroSuc === nroSuc);
    return suc ? `${nroSuc} - ${suc.nombreSucursal}` : String(nroSuc);
  };

  const renderDesktopRow = (conteo: IConteoStock) => {
    const ubicacion = formatUbicacionLabel(conteo.nroUbicacion, conteo.detUbicacion, ubicacionesMap);
    const sucursal = nombreSucursal(conteo.nroSuc);
    const fecha = formatFecha(conteo.fecha);
    const descripcion = conteo.descripcion || '-';
    const usuario = conteo.usuario || '-';

    const cell = (value: string | number, sx?: object) => {
      const text = String(value ?? '-');
      return (
        <TableCell sx={sx}>
          <Tooltip title={text} arrow enterDelay={200} placement="top">
            <Box
              component="span"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {text}
            </Box>
          </Tooltip>
        </TableCell>
      );
    };

    return (
      <TableRow
        key={conteo.nroFormulario}
        hover
        sx={{ cursor: 'pointer' }}
        onClick={() => openDetalle(conteo.nroFormulario)}
      >
        {cell(conteo.nroFormulario)}
        {cell(fecha)}
        {cell(sucursal, { maxWidth: 180 })}
        {cell(ubicacion, { maxWidth: 220, fontWeight: ubicacion !== '-' ? 500 : 400 })}
        {cell(descripcion, { maxWidth: 260 })}
        {cell(usuario)}
      </TableRow>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Conteo de Stock
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/inventario/conteo/nuevo')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Nuevo Conteo
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              flexWrap: 'wrap',
              gap: 2,
              mb: 3,
              alignItems: { lg: 'flex-start' },
            }}
          >
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{
                flex: { lg: '1 1 200px' },
                minWidth: { xs: '100%', lg: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Fecha desde"
              type="date"
              variant="outlined"
              size="small"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: '100%', sm: 160 },
                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
              }}
            />
            <TextField
              label="Fecha hasta"
              type="date"
              variant="outlined"
              size="small"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                minWidth: { xs: '100%', sm: 160 },
                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
              }}
            />
            <FiltroSucursalVentaSelect
              value={filtroNroSuc}
              onChange={(v) => {
                setFiltroNroSuc(v);
                setPage(0);
              }}
              loading={loadingSucursales}
              sucursales={sucursales}
              size="small"
              sx={{
                minWidth: { xs: '100%', sm: 220 },
                width: { xs: '100%', sm: 220 },
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {conteos.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No se encontraron conteos
                </Typography>
              ) : (
                conteos.map((conteo) => (
                    <Card
                      key={conteo.nroFormulario}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                      }}
                      onClick={() => openDetalle(conteo.nroFormulario)}
                    >
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight={600} color="#1976d2">
                            Nro: {conteo.nroFormulario}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Fecha:</strong> {formatFecha(conteo.fecha)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Sucursal:</strong> {nombreSucursal(conteo.nroSuc)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ubicación:</strong>{' '}
                            {formatUbicacionLabel(
                              conteo.nroUbicacion,
                              conteo.detUbicacion,
                              ubicacionesMap,
                            )}
                          </Typography>
                          {conteo.descripcion && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Descripción:</strong> {conteo.descripcion}
                            </Typography>
                          )}
                          {conteo.usuario && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Usuario:</strong> {conteo.usuario}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                ))
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nro</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell>Ubicación</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conteos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No se encontraron conteos
                      </TableCell>
                    </TableRow>
                  ) : (
                    conteos.map((conteo) => renderDesktopRow(conteo))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20]}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConteoStock;
